import React, { useState, useRef, useEffect } from "react";
import QRCodeRenderer from "../shared/QRCodeRenderer";
import Button from "../shared/Button";
import DownloadIcon from "../../assets/icons/DownloadIcon";
import CardHeader, { MenuItem } from "./cards/CardHeader";
import BaseCard from "./cards/BaseCard";
import CardContent from "./cards/CardContent";

interface WifiEntryProps {
  id: string;
  name: string;
  ssid: string;
  encryptionType: string;
  qrString: string;
  onDelete: () => void;
  onDownload: (wifiId: string, format: "png" | "svg") => void;
}

const WifiEntry: React.FC<WifiEntryProps> = ({
  id,
  name,
  ssid,
  encryptionType,
  qrString,
  onDelete,
  onDownload,
}) => {
  const [downloadMenuOpen, setDownloadMenuOpen] = useState(false);
  const downloadRef = useRef<HTMLDivElement>(null);

  const logoUrl = "/images/CGI-Logo.jpg";

  useEffect(() => {
    if (!downloadMenuOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (
        downloadRef.current &&
        !downloadRef.current.contains(e.target as Node)
      ) {
        setDownloadMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [downloadMenuOpen]);

  const handleDownloadSelect = (format: "png" | "svg") => {
    onDownload(id, format);
    setDownloadMenuOpen(false);
  };

  const menuItems: MenuItem[] = [
    {
      label: "Delete",
      onClick: onDelete,
      variant: "danger",
    },
  ];

  const renderInfoSection = () => (
    <>
      <div className="card-info-lines" style={{ marginTop: 40 }}>
        <div>
          <span className="card-label">SSID:</span>
          <span className="card-value">{ssid}</span>
        </div>
        <div>
          <span className="card-label">Encryption:</span>
          <span className="card-value">{encryptionType}</span>
        </div>
      </div>

      <div className="card-actions">
        <div className="card-download-wrapper" ref={downloadRef}>
          <Button
            className="card-btn"
            onClick={(e) => {
              e.stopPropagation();
              setDownloadMenuOpen((o) => !o);
            }}
            variant="secondary"
            type="button"
          >
            Download
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                marginLeft: 6,
                color: "#5236ab",
              }}
            >
              <DownloadIcon />
            </span>
          </Button>
          {downloadMenuOpen && (
            <div className="card-download-dropdown">
              <button
                className="card-download-option"
                onClick={() => handleDownloadSelect("png")}
              >
                PNG
              </button>
              <button
                className="card-download-option"
                onClick={() => handleDownloadSelect("svg")}
              >
                SVG
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );

  return (
    <BaseCard
      id={id}
      renderHeader={({ isExpanded, onToggle }) => (
        <CardHeader
          title={name}
          isExpanded={isExpanded}
          onToggle={onToggle}
          menuItems={menuItems}
        />
      )}
    >
      <CardContent
        qrSection={
          <div className="card-qr-image" id={`qr-code-${id}`}>
            <QRCodeRenderer
              value={qrString}
              size={200}
              withLogo
              logoUrl={logoUrl}
            />
          </div>
        }
        infoSection={renderInfoSection()}
      />
    </BaseCard>
  );
};

export default WifiEntry;
